import { useEffect,useRef,useState } from "react";
import { Box,Typography,Button,CircularProgress,TextField } from "@mui/material";
import CloudUploadRoundedIcon from "@mui/icons-material/CloudUploadRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import { useNavigate } from "react-router-dom";
import { useAppTheme } from "../../../context/ThemeContext";
import { useAlert } from "../../../context/AlertContext";
import { useAuth } from "../../../context/AuthContext";

export default function Team(){
  const navigate=useNavigate();
  const {colors,typography,shape}=useAppTheme();
  const {showAlert}=useAlert();
  const {loadUser}=useAuth();
  const fileInputRef=useRef(null);
  const [team,setTeam]=useState(null);
  const [phone,setPhone]=useState("");
  const [loading,setLoading]=useState(true);
  const [uploading,setUploading]=useState(false);
  const [savingPhone,setSavingPhone]=useState(false);

  const getCookie=(name)=>{
    const value=`; ${document.cookie}`;
    const parts=value.split(`; ${name}=`);
    if(parts.length===2) return parts.pop().split(";").shift();
    return "";
  };

  const loadTeam=async()=>{
    setLoading(true);

    try{
      const response=await fetch("/competition/delegate/team/",{
        method:"GET",
        credentials:"include",
        headers:{"Accept":"application/json"},
      });

      const data=await response.json();

      if(!response.ok) throw new Error(data?.error||"No se pudo cargar la información del equipo.");

      setTeam(data);
      setPhone(data?.telefono||"");
    }catch(error){
      setTeam(null);
      showAlert(error.message||"No se pudo cargar la información del equipo.","error");
    }finally{
      setLoading(false);
    }
  };

  useEffect(()=>{
    loadTeam();
  },[]);

  const handleLogoChange=async(event)=>{
    const file=event.target.files?.[0];
    event.target.value="";

    if(!file) return;

    if(!file.type.startsWith("image/")){
      showAlert("Selecciona una imagen válida.","warning");
      return;
    }

    const formData=new FormData();
    formData.append("logo",file);
    setUploading(true);

    try{
      const response=await fetch("/competition/delegate/team/",{
        method:"PATCH",
        credentials:"include",
        headers:{"X-CSRFToken":getCookie("csrftoken")},
        body:formData,
      });

      const data=await response.json();

      if(!response.ok) throw new Error(data?.error||"No se pudo actualizar el logo.");

      setTeam(data);
      setPhone(data?.telefono||phone);
      showAlert("Logo actualizado correctamente.","success");
    }catch(error){
      showAlert(error.message||"No se pudo actualizar el logo.","error");
    }finally{
      setUploading(false);
    }
  };

  const handleSavePhone=async()=>{
    const cleanPhone=phone.trim();

    if(!cleanPhone){
      showAlert("Ingresa un número de contacto.","warning");
      return;
    }

    const formData=new FormData();
    formData.append("telefono",cleanPhone);
    setSavingPhone(true);

    try{
      const response=await fetch("/competition/delegate/team/",{
        method:"PATCH",
        credentials:"include",
        headers:{"X-CSRFToken":getCookie("csrftoken")},
        body:formData,
      });

      const data=await response.json();

      if(!response.ok) throw new Error(data?.error||"No se pudo actualizar el número de contacto.");

      setTeam(data);
      setPhone(data.telefono||cleanPhone);
      if(loadUser) await loadUser();
      showAlert("Número de contacto actualizado correctamente.","success");
    }catch(error){
      showAlert(error.message||"No se pudo actualizar el número de contacto.","error");
    }finally{
      setSavingPhone(false);
    }
  };

  if(loading){
    return (
      <Box sx={{minHeight:300,display:"flex",alignItems:"center",justifyContent:"center"}}>
        <CircularProgress size={30} sx={{color:colors.controlActive}} />
      </Box>
    );
  }

  if(!team){
    return (
      <Box sx={{width:"100%",maxWidth:1300,mx:"auto"}}>
        <Typography sx={{fontSize:{xs:typography.sizes.xl,md:typography.sizes.pageTitle},fontWeight:typography.weights.extraBold,color:colors.text}}>
          Mi equipo
        </Typography>

        <Box sx={{mt:2,p:2,border:`1px solid ${colors.border}`,borderRadius:shape.radiusLarge,backgroundColor:colors.surface}}>
          <Typography sx={{fontSize:typography.sizes.md,color:colors.textSecondary}}>
            No tienes un equipo asignado actualmente.
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{width:"100%",maxWidth:1300,mx:"auto"}}>
      <Box sx={{mb:2.5}}>
        <Typography sx={{fontSize:{xs:typography.sizes.xl,md:typography.sizes.pageTitle},fontWeight:typography.weights.extraBold,color:colors.text}}>
          Mi equipo
        </Typography>
        <Typography sx={{mt:0.4,fontSize:typography.sizes.md,color:colors.textSecondary}}>
          Administra el logo y tu número de contacto.
        </Typography>
      </Box>

      <Box sx={{display:"grid",gridTemplateColumns:{xs:"1fr",md:"260px 1fr"},gap:2,alignItems:"start"}}>
        <Box sx={{p:2,border:`1px solid ${colors.border}`,borderRadius:shape.radiusLarge,backgroundColor:colors.surface,display:"flex",flexDirection:"column",alignItems:"center"}}>
          <Box sx={{width:150,height:150,border:`1px solid ${colors.border}`,borderRadius:shape.radiusLarge,backgroundColor:colors.backgroundSoft,display:"flex",alignItems:"center",justifyContent:"center",overflow:"hidden"}}>
            {team.logo?(
              <Box component="img" src={team.logo} alt={`Logo de ${team.name}`} sx={{width:"100%",height:"100%",objectFit:"contain",p:1}} />
            ):(
              <GroupsRoundedIcon sx={{fontSize:54,color:colors.textMuted}} />
            )}
          </Box>

          <input ref={fileInputRef} type="file" accept="image/*" hidden onChange={handleLogoChange} />

          <Button
            size="small"
            variant="contained"
            disabled={uploading}
            startIcon={uploading?<CircularProgress size={14} sx={{color:colors.buttonPrimaryText}} />:<CloudUploadRoundedIcon />}
            onClick={()=>fileInputRef.current?.click()}
            sx={{
              mt:1.5,
              minWidth:150,
              borderRadius:shape.radiusSmall,
              textTransform:"none",
              fontWeight:typography.weights.bold,
              backgroundColor:colors.buttonPrimary,
              color:colors.buttonPrimaryText,
              boxShadow:"none",
              "&:hover":{backgroundColor:colors.buttonPrimaryHover,boxShadow:"none"},
              "&.Mui-disabled":{backgroundColor:colors.buttonPrimary,color:colors.buttonPrimaryText,opacity:0.6},
            }}
          >
            {uploading?"Subiendo...":team.logo?"Cambiar logo":"Subir logo"}
          </Button>

          <Typography sx={{mt:1,fontSize:typography.sizes.xs,color:colors.textMuted,textAlign:"center"}}>
            Logo oficial del equipo.
          </Typography>
        </Box>

        <Box sx={{p:{xs:1.5,sm:2},border:`1px solid ${colors.border}`,borderRadius:shape.radiusLarge,backgroundColor:colors.surface}}>
          <Typography sx={{fontSize:typography.sizes.xl,fontWeight:typography.weights.extraBold,color:colors.text}}>
            {team.name}
          </Typography>

          <Typography sx={{mt:0.35,fontSize:typography.sizes.sm,fontWeight:typography.weights.semibold,color:team.active?colors.success:colors.error}}>
            {team.active?"Equipo activo":"Equipo inactivo"}
          </Typography>

          <Box sx={{mt:2}}>
            <Typography sx={{mb:0.7,fontSize:typography.sizes.sm,fontWeight:typography.weights.bold,color:colors.text}}>
              Número de contacto
            </Typography>

            <Box sx={{display:"flex",flexDirection:{xs:"column",sm:"row"},alignItems:"flex-start",gap:1}}>
              <TextField
                size="small"
                value={phone}
                onChange={(event)=>setPhone(event.target.value)}
                placeholder="Número de contacto"
                disabled={savingPhone}
                sx={{
                  width:{xs:"100%",sm:280},
                  "& .MuiOutlinedInput-root":{
                    backgroundColor:colors.inputBackground,
                    color:colors.inputText,
                    "& fieldset":{borderColor:colors.border},
                    "&:hover fieldset":{borderColor:colors.borderDark},
                    "&.Mui-focused fieldset":{borderColor:colors.controlActive},
                  },
                }}
              />

              <Button
                size="small"
                variant="contained"
                startIcon={savingPhone?<CircularProgress size={14} sx={{color:colors.buttonPrimaryText}} />:<SaveRoundedIcon />}
                disabled={savingPhone}
                onClick={handleSavePhone}
                sx={{
                  minHeight:40,
                  borderRadius:shape.radiusSmall,
                  textTransform:"none",
                  fontWeight:typography.weights.bold,
                  backgroundColor:colors.buttonPrimary,
                  color:colors.buttonPrimaryText,
                  boxShadow:"none",
                  "&:hover":{backgroundColor:colors.buttonPrimaryHover,boxShadow:"none"},
                }}
              >
                {savingPhone?"Guardando...":"Guardar"}
              </Button>
            </Box>
          </Box>

          <Box sx={{mt:2.5,pt:2,borderTop:`1px solid ${colors.border}`}}>
            <Typography sx={{fontSize:typography.sizes.lg,fontWeight:typography.weights.bold,color:colors.text}}>
              Categorías asignadas
            </Typography>

            <Typography sx={{mt:0.3,fontSize:typography.sizes.sm,color:colors.textSecondary}}>
              Las categorías son asignadas por la administración del torneo.
            </Typography>

            {team.categories?.length>0?(
              <Box sx={{mt:1.5,display:"grid",gridTemplateColumns:{xs:"1fr",sm:"repeat(2,1fr)"},gap:1}}>
                {team.categories.map((category)=>(
                  <Box key={category.slug} sx={{px:1.4,py:1.2,border:`1px solid ${colors.border}`,borderRadius:shape.radiusSmall,backgroundColor:colors.backgroundSoft}}>
                    <Typography sx={{fontSize:typography.sizes.md,fontWeight:typography.weights.bold,color:colors.text}}>
                      {category.name}
                    </Typography>
                  </Box>
                ))}
              </Box>
            ):(
              <Box sx={{mt:1.5,p:1.5,border:`1px solid ${colors.border}`,borderRadius:shape.radiusSmall,backgroundColor:colors.backgroundSoft}}>
                <Typography sx={{fontSize:typography.sizes.sm,color:colors.textSecondary}}>
                  No existen categorías activas asignadas.
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}